/**
 * CodableNew — wizard pro založení Codable projektu.
 * Dva režimy:
 *   - "Nový": form → orchestrator.project_create; UI polluje uloženou operaci
 *   - "Import": list GitLab projektů usera → orchestrator.project_create(existing repo)
 * Missing GitLab PAT → GitlabOnboardingModal automaticky.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  GitBranch,
  HardDrive,
  LayoutTemplate,
  Link2,
  Loader2,
  Search,
  Wand2,
} from 'lucide-react';
import { CodableIcon } from '../../../components/icons/CodableIcon';
import { api, ApiClientError } from '../../../lib/api';
import { operationPollInterval } from '../../../lib/bounded-polling';
import { pickLiveOperation } from '../../../lib/lifecycle-operation';
import { Badge, Button, Card, CardContent, Input, PageHeader } from '../../../components/ui';
import {
  applyOperationPhase,
  plannedStack,
  stackFromWizardSpec,
  StackFlowGraphic,
} from '../../../components/StackFlowGraphic';
import {
  GitlabConnectionSelect,
  GitlabPrerequisites,
  type ImportMode,
  isGitlabFinding,
  type PreflightResp,
  ProjectWorkerChoice,
  useProjectPreflight,
} from './GitlabImportAccess';
import {
  forgetImport,
  importResumeKey,
  lastImportResume,
  rememberImport,
} from '../../../lib/codable-import-resume';
import { ProjectMembersPanel } from './ProjectMembersPanel';
import GitlabOnboardingModal from './GitlabOnboardingModal';
import AcmImportModal from './AcmImportModal';
import { LovableImportWizard } from '../apphost-lovable';

interface StartResp {
  ok: boolean;
  operation_id?: string;
  task_id?: string;
  run_id?: string;
  status?: string;
  phase?: string;
  progress?: number;
  id?: string;
  slug?: string;
  apply_status?: string;
  apply_results?: unknown;
  publish_ok?: boolean;
  publish_domain?: string;
  publish_error?: string;
  error?: string;
  hint?: string;
  /** Projekce existuje i po selhání apply → projekt lze smazat běžnou cestou. */
  cleanup_hint?: string;
  /** Pre-flight: co brání stavbě, s návodem co udělat. */
  preflight?: {
    blockers?: Array<{ code: string; service: string | null; node: string | null; message: string }>;
    warnings?: Array<{ code: string; message: string }>;
    budget?: { total?: { cpus?: number; memory_mb?: number } } | null;
  };
  setup_required?: string;
}

interface ProjectOperation {
  id: string;
  slug: string;
  operation_type?: 'create' | 'sync' | 'promote' | 'delete' | 'topology-set' | 'build';
  status: 'draft' | 'queued' | 'running' | 'completed' | 'failed';
  phase: string;
  progress: number;
  error?: string | null;
  result?: StartResp | null;
}

interface ProjectOperationListResp {
  ok: boolean;
  operations?: ProjectOperation[];
}

interface ProjectOperationResp {
  ok: boolean;
  operation?: ProjectOperation;
  error?: string;
}

interface UserProject {
  id: number;
  name: string;
  path: string;
  description: string | null;
  http_url: string;
  default_branch: string | null;
  last_activity_at: string;
  already_imported: boolean;
  connection_id?: string;
  host?: string;
}

interface GitlabConnectionStatus {
  id: string;
  host: string;
  ok: boolean;
  error?: string;
  count: number;
}

interface RepoAnalysis {
  ok: boolean;
  error?: string;
  frontend?: {
    type: 'react' | 'node' | 'deno' | 'laravel' | 'none';
    framework?: string;
    build_cmd?: string;
    build_output?: string;
    app_root?: string;
  };
  backend?: {
    type: 'supabase' | 'postgres' | 'mysql' | 'mariadb' | 'none';
    evidence?: string[];
  };
  lovable?: { detected?: boolean; cloud_url?: string };
  /** Značka `.acm/project.json` — repo už spravuje ACM. */
  acm?: {
    managed?: boolean;
    marker?: {
      project_id: string;
      slug: string;
      tenant_id: string | null;
      acm_version: string;
      node: string | null;
      created_at: string;
      source: string;
    } | null;
  };
  source?: 'lovable' | 'native';
  /** Průzkum přístupů — co bude potřeba sehnat, než projekt poběží. */
  access?: Array<{
    code: string;
    scope: 'app' | 'function' | 'source' | 'repo';
    from: 'platform' | 'user';
    label: string;
    evidence?: string;
    optional?: boolean;
    transferable: boolean;
    note?: string;
  }>;
  confidence?: 'high' | 'medium' | 'low';
  evidence?: string[];
  questions?: string[];
  env_keys?: string[];
  migrations?: string[];
  files_read?: string[];
  ai_help?: {
    default_profile_id?: string | null;
    profiles?: Array<{ id: string; label: string }>;
  };
  ai?: {
    ok?: boolean;
    error?: string;
    profile_id?: string;
    suggestion?: {
      frontend?: string;
      backend?: string;
      framework?: string;
      notes?: string;
    };
  };
}

interface ProjectsListResp {
  ok: boolean;
  total?: number;
  projects?: UserProject[];
  connections?: GitlabConnectionStatus[];
  error?: string;
  setup_required?: string;
}

type Tab = 'new' | 'import';
/** Podsekce Import průvodce */
type ImportSource = 'choose' | 'gitlab' | 'lovable' | 'acm' | 'giturl';

interface TemplateMeta {
  id: string;
  name?: string;
  label: string;
  description: string;
  category?: string;
  backend_default?: 'supabase' | 'none';
  template_url?: string;
}

interface TemplatesResp {
  ok: boolean;
  templates?: TemplateMeta[];
  error?: string;
}

type TopologyTarget = 'beta' | 'simple' | 'normal' | 'full_split';

const TOPOLOGY_ORDER: TopologyTarget[] = ['beta', 'simple', 'normal', 'full_split'];

const TOPOLOGY_LABEL_KEY: Record<TopologyTarget, string> = {
  beta: 'codable.wsTopologyBeta',
  simple: 'codable.wsTopologySimple',
  normal: 'codable.wsTopologyNormal',
  full_split: 'codable.wsTopologyFullSplit',
};

interface TopologyOptionsResp {
  ok: boolean;
  allowed?: TopologyTarget[];
  locked?: TopologyTarget[];
  error?: string;
}

export default function CodableNew() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() =>
    searchParams.get('tab') === 'import' ? 'import' : 'new'
  );
  const [importSource, setImportSource] = useState<ImportSource>(() => {
    const s = searchParams.get('from');
    if (s === 'gitlab' || s === 'lovable' || s === 'acm' || s === 'giturl') return s;
    return 'choose';
  });
  const [importAsLovable, setImportAsLovable] = useState(false);
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [gitlabConnection, setGitlabConnection] = useState('');
  const [importMode, setImportMode] = useState<ImportMode>('existing');
  const [templateId, setTemplateId] = useState<string>(
    () => searchParams.get('template') || 'react-vite',
  );
  const [backendType, setBackendType] = useState<'supabase' | 'none'>(() => {
    const b = searchParams.get('backend');
    return b === 'none' || b === 'supabase' ? b : 'supabase';
  });
  const [search, setSearch] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [backendTouched, setBackendTouched] = useState(false);
  const [activeOperationId, setActiveOperationId] = useState<string | null>(
    null,
  );
  const [handledTerminalOperation, setHandledTerminalOperation] = useState<
    string | null
  >(null);
  /**
   * Poslední krok průvodce. Průvodce dřív po dokončení hned přesměroval do
   * workspace a přidělení práv na projekt zůstalo schované pod ozubeným kolem —
   * importující člen byl jediný, kdo projekt viděl. Závěrečná karta drží obojí:
   * členy přidáš tady a do Codable jdeš, až budeš chtít.
   */
  const [finished, setFinished] = useState<
    { id: string; slug: string } | null
  >(null);
  /** Varianta projektu se volí až v posledním kroku — import ji nedeklaruje. */
  const [variantDraft, setVariantDraft] = useState<TopologyTarget>('normal');
  const [variantDomain, setVariantDomain] = useState('');
  const [variantSaved, setVariantSaved] = useState(false);
  /** Lovable / git URL import fields */
  const [lovableUrl, setLovableUrl] = useState('');
  const [lovableSlug, setLovableSlug] = useState('');
  const [lovableSpec, setLovableSpec] = useState<{ services?: Record<string, unknown> } | null>(null);
  const [lovableSpecError, setLovableSpecError] = useState<string | null>(null);
  const [gitUrl, setGitUrl] = useState('');
  const [gitUrlSlug, setGitUrlSlug] = useState('');
  const [acmModalOpen, setAcmModalOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [onboardingIntent, setOnboardingIntent] = useState<'create' | 'list'>('create');
  const [inspectProject, setInspectProject] = useState<UserProject | null>(null);
  const [frontendPick, setFrontendPick] = useState<
    'react' | 'node' | 'deno' | 'laravel' | 'none'
  >('react');
  const [backendPick, setBackendPick] = useState<
    'supabase' | 'postgres' | 'mysql' | 'mariadb' | 'none'
  >('supabase');
  const [aiProfileId, setAiProfileId] = useState('');
  const [wizardInitial, setWizardInitial] = useState<{
    git_repo?: string;
    apphost_name?: string;
    git_branch?: string;
    source?: 'lovable-cloud' | 'git' | 'acm-supabase';
  } | undefined>(undefined);

  /**
   * Krok PREREKVIZITY (Filip 2026-09-13) — dvě situace, stejná otázka.
   *
   * Import vybraného GitLab projektu se ptá na práva v tom repozitáři a podle
   * módu i na právo založit klon/fork. Zakládání nového projektu se ptá jen na
   * to, jestli volající v daném GitLabu vůbec smí zakládat. Import z URL
   * (Lovable, cizí git) je taky `create`: zdroj není v našem GitLabu, takže se
   * do něj nic nezapisuje — jen vznikne nový projekt a obsah se do něj nahraje.
   */
  const importPreflight = useProjectPreflight({
    mode: importMode,
    backend: backendPick,
    frontend: frontendPick,
    projectId: inspectProject?.id ?? null,
    connection: inspectProject?.connection_id ?? null,
    enabled: !!inspectProject,
  });
  const createPreflight = useProjectPreflight({
    mode: 'create',
    backend: backendType,
    frontend: 'react',
    connection: gitlabConnection || null,
    enabled: !!gitlabConnection && !inspectProject,
  });
  const createBlockers = (createPreflight.data?.blockers ?? []).filter(isGitlabFinding);

  /**
   * Návrat po schválení práv. Kdo odešle žádost, z průvodce odejde — a po
   * schválení se vracel na prázdný seznam projektů. Vybraný projekt a mód se
   * proto drží v prohlížeči a průvodce se otevře tam, kde člověk skončil.
   * Výslovný odkaz s `?tab=` má přednost — ten člověk ví, kam jde.
   */
  useEffect(() => {
    if (searchParams.get('tab') || searchParams.get('from')) return;
    const saved = lastImportResume<UserProject>();
    if (!saved?.pending_approval || !saved.project?.id) return;
    setTab('import');
    setImportSource('gitlab');
    setInspectProject(saved.project);
    if (saved.mode === 'existing' || saved.mode === 'clone' || saved.mode === 'fork') {
      setImportMode(saved.mode);
    }
    // Jen při otevření průvodce; pozdější kliknutí uživatele nepřepisovat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rozdělaný výběr přežije reload i odchod na schválení práv.
  useEffect(() => {
    if (!inspectProject) return;
    rememberImport({
      key: importResumeKey(inspectProject.connection_id, inspectProject.id),
      project: inspectProject,
      mode: importMode,
      saved_at: new Date().toISOString(),
    });
  }, [inspectProject, importMode]);

  // Reload/browser reconnect must not detach the user from a persisted create.
  const recoverOperationQ = useQuery({
    queryKey: ['orchestrator-project-operations', 'mine'],
    queryFn: () =>
      api.mcp<ProjectOperationListResp>('orchestrator.project_operation_list', {
        mine: true,
        limit: 20,
      }),
    staleTime: 5_000,
  });
  useEffect(() => {
    if (activeOperationId) return;
    const recoverable = pickLiveOperation(recoverOperationQ.data?.operations, {
      matches: (operation) => (operation.operation_type ?? 'create') === 'create',
      live: ['draft', 'queued', 'running', 'failed'],
    });
    if (recoverable) setActiveOperationId(recoverable.id);
  }, [activeOperationId, recoverOperationQ.data?.operations]);

  const templatesQ = useQuery({
    queryKey: ['codable-templates'],
    queryFn: () => api.mcp<TemplatesResp>('codable.template_list', {}),
    staleTime: 60_000,
  });

  const templates: TemplateMeta[] = useMemo(() => {
    const list = templatesQ.data?.templates ?? [];
    const localize = (tpl: TemplateMeta): TemplateMeta => {
      const idKey = tpl.id.replace(/-/g, '_');
      return {
        ...tpl,
        label: t(`codable.tplLabel_${idKey}`, tpl.label),
        description: t(`codable.tplDesc_${idKey}`, tpl.description),
      };
    };
    if (list.length > 0) return list.map(localize);
    // Fallback když API ještě není na nodu
    return [
      {
        id: 'react-vite',
        label: t('codable.tplLabel_react_vite', t('codable.emptyProject')),
        description: t('codable.tplDesc_react_vite', t('codable.emptyHint')),
        backend_default: 'supabase',
      },
    ];
  }, [templatesQ.data?.templates, t]);

  // Z query / výběru šablony nastav recommended backend (dokud user nepřepne ručně)
  useEffect(() => {
    if (backendTouched) return;
    const tpl = templates.find((x) => x.id === templateId);
    if (tpl?.backend_default) setBackendType(tpl.backend_default);
  }, [templateId, templates, backendTouched]);

  // Import tab: načti user's GitLab projekty
  const userProjects = useQuery({
    queryKey: ['codable-user-projects', search],
    queryFn: () =>
      api.mcp<ProjectsListResp>('codable.gitlab_user_projects', {
        search: search.trim() || undefined,
        limit: 50,
      }),
    enabled: tab === 'new' || (tab === 'import' && importSource === 'gitlab'),
    staleTime: 30_000,
  });

  const startMut = useMutation({
    mutationFn: async (mode: 'apply' | 'draft') => {
      const tpl = templates.find((x) => x.id === templateId);
      const request = {
        version: 1,
        source: { consumer: 'codable', channel: 'ui' },
        project: {
          ...(slug.trim() ? { slug: slug.trim() } : {}),
          ...(description.trim() ? { description: description.trim() } : {}),
        },
        repository: {
          provider: 'gitlab',
          mode: 'create',
          connection_id: gitlabConnection || undefined,
          visibility: 'internal',
          initialize_with_readme: true,
        },
        template: tpl?.template_url ? { kind: 'repository', source_url: tpl.template_url } : {
          kind: 'builtin',
          name: tpl?.id === 'blank' ? 'react-vite' : tpl?.id ?? 'react-vite',
        },
        runtime: {
          frontend: {
            type: 'react',
            run_mode: 'dev',
            build_cmd: 'npm install && npm run build',
          },
          backend: { type: backendType },
        },
        publication: { frontend: { enabled: true } },
        codable: { enabled: true },
      };
      return api.mcp<StartResp>('orchestrator.project_create', { mode, request });
    },
    onSuccess: (r) => {
      if (r.ok && r.operation_id) {
        setHandledTerminalOperation(null);
        setActiveOperationId(r.operation_id);
        if (r.status === 'draft') {
          toast.success(t('codable.draftSaved', 'Zadání {{slug}} je uložené bez realizace.', { slug: r.slug ?? '' }));
        }
        return;
      }
      if (r.setup_required === 'gitlab_credentials') {
        setOnboardingIntent('create');
        setShowOnboarding(true);
        return;
      }
      toast.error(r.error ?? t('codable.unknownError'));
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiClientError ? e.message : String(e);
      toast.error(t('codable.errorGeneric', { msg }));
    },
  });

  const operationQ = useQuery({
    queryKey: ['orchestrator-project-operation', activeOperationId],
    queryFn: () =>
      api.mcp<ProjectOperationResp>('orchestrator.project_operation_get', {
        operation_id: activeOperationId,
      }),
    enabled: !!activeOperationId,
    refetchInterval: (query) =>
      operationPollInterval(
        query.state.data?.operation?.status,
        query.state.dataUpdateCount,
      ),
  });

  const activeOperation = operationQ.data?.operation;
  const operationLoadError = operationQ.isError
    ? (operationQ.error instanceof Error ? operationQ.error.message : String(operationQ.error))
    : operationQ.data?.ok === false
    ? operationQ.data.error ?? t('codable.unknownError')
    : null;
  useEffect(() => {
    if (!activeOperation || handledTerminalOperation === activeOperation.id) {
      return;
    }
    if (activeOperation.status === 'completed') {
      setHandledTerminalOperation(activeOperation.id);
      if (activeOperation.operation_type === 'topology-set') {
        setVariantSaved(true);
        toast.success(t('codable.topologyDone', 'Varianta projektu je nastavená'));
        return;
      }
      const result = activeOperation.result;
      if (result?.id) {
        toast.success(
          t('codable.projectCreated', {
            slug: result.slug ?? activeOperation.slug,
          }),
        );
        setFinished({
          id: result.id,
          slug: result.slug ?? activeOperation.slug,
        });
      } else {
        toast.error(t('codable.unknownError'));
      }
    } else if (activeOperation.status === 'failed') {
      setHandledTerminalOperation(activeOperation.id);
      if (activeOperation.result?.setup_required === 'gitlab_credentials') {
        setOnboardingIntent('create');
        setShowOnboarding(true);
      }
      toast.error(
        activeOperation.error ?? activeOperation.result?.error ??
          t('codable.unknownError'),
      );
    }
  }, [activeOperation, handledTerminalOperation, nav, t]);

  // Schválené varianty tenanta. Zamčené se v průvodci ukazují, ale nejdou
  // zvolit — ať je vidět, co si tenant může doobjednat.
  const topologyOptionsQ = useQuery({
    queryKey: ['topology-options'],
    enabled: !!finished,
    queryFn: () => api.mcp<TopologyOptionsResp>('orchestrator.topology_options', {}),
  });
  const allowedTargets = topologyOptionsQ.data?.ok
    ? topologyOptionsQ.data.allowed ?? TOPOLOGY_ORDER
    : TOPOLOGY_ORDER;

  /** Projekt založený průvodcem — potřebujeme jeho orchestrátorové ID. */
  const finishedProjectQ = useQuery({
    queryKey: ['codable-project', finished?.id],
    enabled: !!finished?.id,
    queryFn: () =>
      api.mcp<{ ok: boolean; orchestrator_project_id?: string | null }>(
        'codable.project_get',
        { id: finished!.id },
      ),
  });

  const setVariantMut = useMutation({
    mutationFn: async () => {
      const prj = finishedProjectQ.data?.orchestrator_project_id;
      if (!prj) throw new Error(t('codable.wsTopologyNoOrch'));
      return api.mcp<{ ok: boolean; error?: string; operation_id?: string }>(
        `${prj}.orchestrator.topology-set`,
        {
          target: variantDraft,
          apply: true,
          ...(variantDraft !== 'beta' && variantDomain.trim()
            ? { prod_domain: variantDomain.trim() }
            : {}),
        },
      );
    },
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('codable.errorGeneric', { msg: 'topology-set' }));
        return;
      }
      if (r.operation_id) {
        // Naplnění běží frontou — průběh a výsledek dojdou přes operaci
        // (stejná lišta jako u založení projektu).
        setHandledTerminalOperation(null);
        setActiveOperationId(r.operation_id);
        toast.success(
          t('codable.wsTopologyQueued', 'Varianta se staví — průběh je v liště projektu'),
        );
        return;
      }
      setVariantSaved(true);
      toast.success(t('codable.wsTopologyApplied'));
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : String(e)),
  });

  const retryOperationMut = useMutation({
    mutationFn: () =>
      api.mcp<StartResp>('orchestrator.project_operation_retry', {
        operation_id: activeOperationId,
      }),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error ?? t('codable.unknownError'));
        return;
      }
      setHandledTerminalOperation(null);
      void operationQ.refetch();
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : String(error));
    },
  });

  const startDraftMut = useMutation({
    mutationFn: () =>
      api.mcp<StartResp>('orchestrator.project_operation_start', {
        operation_id: activeOperationId,
      }),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error ?? t('codable.unknownError'));
        return;
      }
      setHandledTerminalOperation(null);
      void operationQ.refetch();
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : String(error));
    },
  });

  const importMut = useMutation({
    mutationFn: (opts: {
      project: UserProject;
      frontend: 'react' | 'node' | 'deno' | 'laravel' | 'none';
      backend: 'supabase' | 'postgres' | 'mysql' | 'mariadb' | 'none';
      build_cmd?: string;
      build_output?: string;
      app_root?: string;
      source: 'lovable' | 'native';
    }) => {
      const project = opts.project;
      const fallback = project.path.split('/').slice(-1)[0] || 'project';
      const projectSlug = (fallback + (importMode === 'existing' ? '' : `-${importMode}`)).toLowerCase().replace(/[^a-z0-9-]/g, '-');
      return api.mcp<StartResp>('orchestrator.project_create', {
        request: {
          version: 1,
          source: { consumer: 'codable', channel: 'ui-import' },
          project: {
            slug: projectSlug,
            ...(project.description ? { description: project.description } : {}),
          },
          repository: {
            provider: 'gitlab',
            mode: importMode,
            project_id: project.id,
            ...(project.connection_id ? { connection_id: project.connection_id } : {}),
          },
          template: { kind: 'existing' },
          runtime: {
            frontend: {
              type: opts.frontend,
              run_mode: opts.frontend === 'react' ? 'dev' : undefined,
              ...(opts.build_cmd ? { build_cmd: opts.build_cmd } : {}),
              ...(opts.build_output ? { build_output: opts.build_output } : {}),
              ...(opts.app_root ? { app_root: opts.app_root } : {}),
            },
            backend: { type: opts.backend },
          },
          publication: { frontend: { enabled: true } },
          codable: {
            enabled: true,
            source: opts.source,
            origin: {},
          },
        },
      });
    },
    onSuccess: (r, vars) => {
      if (r.ok && r.operation_id) {
        // Import běží — rozdělaná volba už není k čemu.
        forgetImport(importResumeKey(vars.project.connection_id, vars.project.id));
        setHandledTerminalOperation(null);
        setActiveOperationId(r.operation_id);
        return;
      }
      if (r.setup_required === 'gitlab_credentials') {
        setOnboardingIntent('list');
        setShowOnboarding(true);
        return;
      }
      toast.error(r.error ?? t('codable.importFailed'));
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiClientError ? e.message : String(e);
      toast.error(t('codable.errorGeneric', { msg }));
    },
  });

  const analyzeMut = useMutation({
    mutationFn: (opts: { project: UserProject; ai?: boolean; profile_id?: string }) =>
      api.mcp<RepoAnalysis>('codable.repo_analyze', {
        project_id: opts.project.id,
        ...(opts.project.connection_id ? { connection_id: opts.project.connection_id } : {}),
        ...(opts.project.default_branch ? { ref: opts.project.default_branch } : {}),
        ...(opts.ai ? { ai: true } : {}),
        ...(opts.profile_id ? { profile_id: opts.profile_id } : {}),
      }),
    onSuccess: (r, vars) => {
      if (!r.ok && r.error) {
        toast.error(r.error);
        return;
      }
      if (r.frontend?.type) setFrontendPick(r.frontend.type);
      if (r.backend?.type) setBackendPick(r.backend.type);
      if (r.source === 'lovable') setImportAsLovable(true);
      if (!vars.ai && r.ai_help?.default_profile_id) {
        setAiProfileId(r.ai_help.default_profile_id);
      }
      if (r.ai?.suggestion?.frontend && ['react', 'node', 'deno', 'laravel', 'none'].includes(r.ai.suggestion.frontend)) {
        setFrontendPick(r.ai.suggestion.frontend as typeof frontendPick);
      }
      if (r.ai?.suggestion?.backend &&
        ['supabase', 'postgres', 'mysql', 'mariadb', 'none'].includes(r.ai.suggestion.backend)
      ) {
        setBackendPick(r.ai.suggestion.backend as typeof backendPick);
      }
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : String(e));
    },
  });

  /** Lovable / generic git URL → orchestrator.project_create (template repository) */
  const urlImportMut = useMutation({
    mutationFn: (opts: {
      gitUrl: string;
      slug?: string;
      source: 'lovable' | 'native';
      channel: string;
    }) => {
      const raw = opts.gitUrl.trim();
      let projectSlug = (opts.slug || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
      if (!projectSlug) {
        try {
          const u = new URL(raw);
          const last = u.pathname.split('/').filter(Boolean).pop() ?? 'project';
          projectSlug = last.replace(/\.git$/i, '').toLowerCase().replace(/[^a-z0-9-]/g, '-');
        } catch {
          projectSlug = 'imported-project';
        }
      }
      projectSlug = projectSlug.replace(/^-+|-+$/g, '') || 'imported-project';
      return api.mcp<StartResp>('orchestrator.project_create', {
        request: {
          version: 1,
          source: { consumer: 'codable', channel: opts.channel },
          project: { slug: projectSlug },
          repository: {
            provider: 'gitlab',
            mode: 'create',
            connection_id: gitlabConnection || undefined,
            visibility: 'internal',
            initialize_with_readme: false,
          },
          template: { kind: 'repository', source_url: raw },
          runtime: {
            frontend: {
              type: 'react',
              run_mode: 'dev',
              build_cmd: 'npm install && npm run build',
            },
            backend: { type: 'supabase' },
          },
          publication: { frontend: { enabled: true } },
          codable: {
            enabled: true,
            source: opts.source,
            origin: { git_url: raw },
          },
        },
      });
    },
    onSuccess: (r) => {
      if (r.ok && r.operation_id) {
        setHandledTerminalOperation(null);
        setActiveOperationId(r.operation_id);
        return;
      }
      if (r.setup_required === 'gitlab_credentials') {
        setOnboardingIntent('create');
        setShowOnboarding(true);
        return;
      }
      toast.error(r.error ?? t('codable.importFailed'));
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiClientError ? e.message : String(e);
      toast.error(t('codable.errorGeneric', { msg }));
    },
  });

  const operationRunning = (!!activeOperationId && !activeOperation && !operationLoadError) ||
    activeOperation?.status === 'queued' ||
    activeOperation?.status === 'running';
  /** Varianta se staví frontou — tlačítko i volba počkají na výsledek. */
  const variantBusy = operationRunning && activeOperation?.operation_type !== 'create';
  const anyLoading = startMut.isPending || startDraftMut.isPending || operationRunning ||
    importMut.isPending || urlImportMut.isPending || analyzeMut.isPending;

  const previewMut = useMutation({
    mutationFn: () =>
      api.mcp<{
        ok: boolean;
        error?: string;
        spec?: { services?: Record<string, unknown> };
      }>('orchestrator.orch_wizard', {
        mode: 'lovable',
        git_repo: lovableUrl.trim(),
        ...(lovableSlug.trim() ? { name: lovableSlug.trim() } : {}),
      }),
    onSuccess: (r) => {
      if (!r.ok || !r.spec) {
        setLovableSpec(null);
        setLovableSpecError(r.error ?? t('codable.unknownError'));
        return;
      }
      setLovableSpecError(null);
      setLovableSpec(r.spec);
    },
    onError: (e: unknown) => {
      setLovableSpec(null);
      setLovableSpecError(e instanceof Error ? e.message : String(e));
    },
  });

  const newTabStack = plannedStack({
    frontend: 'react',
    backend: backendType,
    publish: true,
    backup: backendType !== 'none',
  });
  const operationStack = applyOperationPhase(
    lovableSpec
      ? stackFromWizardSpec(lovableSpec)
      : newTabStack,
    activeOperation?.phase,
    activeOperation?.status,
  );

  // Závěrečný krok: co vzniklo, komu to patří a teprve pak odchod do Codable.
  if (finished) {
    return (
      <div className='p-6 max-w-3xl mx-auto space-y-6'>
        <PageHeader
          title={t('codable.doneTitle', 'Projekt je hotový')}
          description={t('codable.doneDesc', {
            slug: finished.slug,
            defaultValue: '{{slug}} běží. Přidej členy — kdo tu není, projekt v Codable neuvidí.',
          })}
          icon={CodableIcon}
          actions={
            <Link to='/modules/codable'>
              <Button variant='ghost' icon={<ArrowLeft size={16} />}>
                {t('common.back', 'Zpět')}
              </Button>
            </Link>
          }
        />

        <Card>
          <CardContent>
            <div className='flex items-center gap-2 text-sm text-emerald-400'>
              <CheckCircle2 size={16} />
              <span>{t('codable.projectCreated', { slug: finished.slug })}</span>
            </div>
          </CardContent>
        </Card>

        <ProjectWorkerChoice slug={finished.slug} />
        <Card>
          <CardContent className='space-y-3'>
            <div className='text-sm font-medium text-gray-200'>
              {t('codableMembers.projectAccess', 'Vlastník, členové a oprávnění')}
            </div>
            <ProjectMembersPanel
              project={{ id: finished.id, slug: finished.slug, name: finished.slug }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className='space-y-3'>
            <div>
              <div className='text-sm font-medium text-gray-200'>
                {t('codable.wsTopologyTitle')}
              </div>
              <p className='text-xs text-gray-500'>
                {t(
                  'codable.doneVariantHint',
                  'Z čeho se projekt skládá. Zamčené varianty nemá tenant schválené.',
                )}
              </p>
            </div>
            <div className='grid gap-2'>
              {TOPOLOGY_ORDER.map((target) => {
                const approved = allowedTargets.includes(target);
                return (
                  <label
                    key={target}
                    className={`flex items-start gap-2 rounded-lg border px-3 py-2 transition-colors ${
                      !approved
                        ? 'border-gray-800/60 opacity-50 cursor-not-allowed'
                        : variantDraft === target
                        ? 'border-plex/50 bg-plex/5 cursor-pointer'
                        : 'border-gray-800 hover:border-gray-700 cursor-pointer'
                    }`}
                  >
                    <input
                      type='radio'
                      name='wizard-topology'
                      className='mt-1'
                      checked={variantDraft === target}
                      disabled={!approved || setVariantMut.isPending}
                      onChange={() => setVariantDraft(target)}
                    />
                    <span className='text-xs min-w-0 flex-1'>
                      {t(TOPOLOGY_LABEL_KEY[target])}
                    </span>
                    {!approved && (
                      <Badge className='text-[10px]'>
                        {t('codable.doneVariantLocked', 'Neschváleno')}
                      </Badge>
                    )}
                  </label>
                );
              })}
            </div>
            {variantDraft !== 'beta' && (
              <label className='block space-y-1'>
                <span className='text-[11px] text-gray-500'>
                  {t('codable.wsTopologyProdDomain')}
                </span>
                <Input
                  value={variantDomain}
                  onChange={(e) => setVariantDomain(e.target.value)}
                  placeholder='app.example.com'
                  disabled={setVariantMut.isPending}
                  className='font-mono text-xs'
                />
              </label>
            )}
            <Button
              variant='secondary'
              loading={setVariantMut.isPending || variantBusy}
              disabled={setVariantMut.isPending || variantBusy ||
                (variantDraft !== 'beta' && !variantDomain.trim())}
              onClick={() => setVariantMut.mutate()}
            >
              {variantSaved
                ? t('codable.doneVariantAgain', 'Změnit variantu')
                : t('codable.doneVariantApply', 'Nastavit variantu')}
            </Button>
            {activeOperation?.operation_type === 'topology-set' && (
              // Stejná lišta jako v workspace (CodableWorkspace lifecycleOperation).
              <div
                className={`flex items-center gap-3 rounded border px-3 py-2 text-xs ${
                  activeOperation.status === 'failed'
                    ? 'border-red-500/30 bg-red-950/25 text-red-200'
                    : activeOperation.status === 'completed'
                    ? 'border-emerald-500/20 bg-emerald-950/15 text-emerald-200'
                    : 'border-blue-500/20 bg-blue-950/20 text-blue-200'
                }`}
              >
                {operationRunning && <Loader2 size={14} className='animate-spin' />}
                <span className='font-semibold'>
                  {t('codable.opTopologySet', 'Varianta')} · {activeOperation.phase}
                </span>
                <div className='h-1.5 min-w-24 flex-1 overflow-hidden rounded-full bg-gray-900'>
                  <div
                    className={activeOperation.status === 'failed'
                      ? 'h-full bg-red-500'
                      : 'h-full bg-blue-500'}
                    style={{ width: `${Math.max(0, Math.min(100, activeOperation.progress))}%` }}
                  />
                </div>
                <span className='font-mono'>{activeOperation.progress}%</span>
                {activeOperation.error && (
                  <span className='max-w-md truncate text-red-300' title={activeOperation.error}>
                    {activeOperation.error}
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className='flex flex-wrap gap-2'>
          <Button
            variant='primary'
            icon={<CodableIcon size={16} />}
            onClick={() => nav(`/modules/codable/${finished.id}`)}
          >
            {t('codable.doneOpenProject', 'Otevřít projekt v Codable')}
          </Button>
          <Button
            variant='secondary'
            onClick={() => {
              setFinished(null);
              setActiveOperationId(null);
            }}
          >
            {t('codable.doneAnother', 'Založit další projekt')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='p-6 max-w-3xl mx-auto space-y-6'>
      <PageHeader
        title={t('codable.newTitle')}
        description={t('codable.newDesc')}
        icon={CodableIcon}
        actions={
          <Link to='/modules/codable'>
            <Button variant='ghost' icon={<ArrowLeft size={16} />}>
              {t('common.back', 'Zpět')}
            </Button>
          </Link>
        }
      />

      <div className='flex gap-2 border-b border-gray-800'>
        <TabButton
          active={tab === 'new'}
          onClick={() => setTab('new')}
          icon={<CodableIcon size={14} />}
        >
          {t('codable.tabNew')}
        </TabButton>
        <TabButton
          active={tab === 'import'}
          onClick={() => setTab('import')}
          icon={<GitBranch size={14} />}
        >
          {t('codable.tabImport')}
        </TabButton>
      </div>

      {!inspectProject && (tab === 'new' || importSource === 'giturl' || importSource === 'lovable') && (
        <>
          <GitlabConnectionSelect value={gitlabConnection} onChange={setGitlabConnection} />
          {gitlabConnection && (
            <GitlabPrerequisites
              mode='create'
              connection={gitlabConnection}
              preflight={createPreflight}
            />
          )}
        </>
      )}
      {inspectProject && (
        <GitlabPrerequisites
          project={inspectProject.id}
          projectPath={inspectProject.path}
          connection={inspectProject.connection_id}
          mode={importMode}
          onMode={setImportMode}
          preflight={importPreflight}
          onPendingApproval={() =>
            rememberImport({
              key: importResumeKey(inspectProject.connection_id, inspectProject.id),
              project: inspectProject,
              mode: importMode,
              saved_at: new Date().toISOString(),
              pending_approval: true,
            })}
          action={analyzeMut.data
            ? undefined
            : {
              label: t('codable.analyzeStack', 'Analyzovat stack'),
              onClick: () => analyzeMut.mutate({ project: inspectProject }),
              loading: analyzeMut.isPending,
            }}
          onClose={() => {
            forgetImport(importResumeKey(inspectProject.connection_id, inspectProject.id));
            setInspectProject(null);
            analyzeMut.reset();
          }}
        />
      )}
      {inspectProject && (analyzeMut.isPending || analyzeMut.data || analyzeMut.isError) && (
        <RepoAnalysisCard
          project={inspectProject}
          preflight={importPreflight}
          analysis={analyzeMut.data}
          loading={analyzeMut.isPending}
          error={analyzeMut.isError
            ? (analyzeMut.error instanceof Error ? analyzeMut.error.message : String(analyzeMut.error))
            : analyzeMut.data?.ok === false
            ? analyzeMut.data.error ?? null
            : null}
          frontend={frontendPick}
          backend={backendPick}
          onFrontend={setFrontendPick}
          onBackend={setBackendPick}
          importAsLovable={importAsLovable}
          onImportAsLovable={setImportAsLovable}
          aiProfileId={aiProfileId}
          onAiProfile={setAiProfileId}
          onAskAi={() =>
            analyzeMut.mutate({
              project: inspectProject,
              ai: true,
              profile_id: aiProfileId || undefined,
            })}
          askingAi={analyzeMut.isPending && analyzeMut.variables?.ai === true}
          onCreate={() =>
            importMut.mutate({
              project: inspectProject,
              frontend: frontendPick,
              backend: backendPick,
              build_cmd: analyzeMut.data?.frontend?.build_cmd,
              build_output: analyzeMut.data?.frontend?.build_output,
              app_root: analyzeMut.data?.frontend?.app_root,
              source: importAsLovable || analyzeMut.data?.source === 'lovable' ? 'lovable' : 'native',
            })}
          creating={importMut.isPending}
          onCloudWizard={() => {
            const slug = (inspectProject.path.split('/').pop() || inspectProject.name)
              .toLowerCase()
              .replace(/[^a-z0-9-]/g, '-')
              .replace(/^-+|-+$/g, '');
            setWizardInitial({
              git_repo: inspectProject.http_url,
              apphost_name: slug,
              git_branch: inspectProject.default_branch || 'main',
              source: 'lovable-cloud',
            });
            setAdvancedOpen(true);
          }}
          onClose={() => {
            forgetImport(importResumeKey(inspectProject.connection_id, inspectProject.id));
            setInspectProject(null);
            analyzeMut.reset();
          }}
        />
      )}

      {tab === 'new' && (
        <>
        <Card>
          <CardContent>
            <div className='space-y-5'>
              <div>
                <label className='block text-sm font-medium text-gray-300 mb-1.5'>
                  {t('codable.descLabel')}
                </label>
                <textarea
                  className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none min-h-[100px]'
                  placeholder={t('codable.descPh')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={anyLoading}
                />
                <div className='text-xs text-gray-500 mt-1'>
                  {t('codable.descToChat')}
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-300 mb-1.5'>
                  {t('codable.slugLabel')}{' '}
                  <span className='text-gray-500 font-normal'>
                    {t('codable.slugOptional')}
                  </span>
                </label>
                <input
                  className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none font-mono'
                  placeholder={t('codable.slugPh')}
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase())}
                  pattern='[a-z][a-z0-9-]*[a-z0-9]'
                  maxLength={40}
                  disabled={anyLoading}
                />
                <div className='text-xs text-gray-500 mt-1'>
                  {t('codable.slugHint')}
                </div>
              </div>

              <div>
                <div className='mb-1.5 flex items-center justify-between gap-2'>
                  <label className='block text-sm font-medium text-gray-300'>
                    {t('codable.templateLabel')}
                  </label>
                  <Link
                    to='/modules/codable/library?tab=templates'
                    className='inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300'
                  >
                    <LayoutTemplate size={12} />
                    {t('codable.templatesBrowse')}
                  </Link>
                </div>
                <p className='mb-2 text-xs text-gray-500'>
                  {t('codable.templatesPickHint')}
                </p>
                {templatesQ.isLoading && (
                  <div className='flex items-center gap-2 py-3 text-sm text-gray-500'>
                    <Loader2 size={14} className='animate-spin' />
                    {t('common.loading')}
                  </div>
                )}
                <div className='space-y-2'>
                  {templates.map((tpl) => (
                    <label
                      key={tpl.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        templateId === tpl.id
                          ? 'border-blue-500 bg-blue-500/5'
                          : 'border-gray-800 bg-gray-950 hover:border-gray-700'
                      }`}
                    >
                      <input
                        type='radio'
                        name='template'
                        value={tpl.id}
                        checked={templateId === tpl.id}
                        onChange={() => {
                          setTemplateId(tpl.id);
                          if (tpl.backend_default && !backendTouched) {
                            setBackendType(tpl.backend_default);
                          }
                        }}
                        className='mt-1 shrink-0'
                        disabled={anyLoading}
                      />
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2'>
                          <span className='text-sm font-medium text-gray-200'>
                            {tpl.label}
                          </span>
                          {tpl.category && (
                            <span className='rounded border border-gray-800 px-1.5 py-0.5 text-[10px] uppercase text-gray-500'>
                              {t(
                                `codable.tplCat${
                                  tpl.category === 'website'
                                    ? 'Website'
                                    : tpl.category === 'shop'
                                    ? 'Shop'
                                    : tpl.category === 'app'
                                    ? 'App'
                                    : 'Blank'
                                }`,
                                tpl.category,
                              )}
                            </span>
                          )}
                        </div>
                        <div className='text-xs text-gray-500 mt-0.5'>
                          {tpl.description}
                        </div>
                        {tpl.template_url && (
                          <div className='text-[10px] text-gray-600 font-mono mt-1 truncate'>
                            {t('codable.sourceUrl', { url: tpl.template_url })}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-300 mb-1.5'>
                  {t('codable.backend')}
                </label>
                <div className='space-y-2'>
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      backendType === 'supabase'
                        ? 'border-blue-500 bg-blue-500/5'
                        : 'border-gray-800 bg-gray-950 hover:border-gray-700'
                    }`}
                  >
                    <input
                      type='radio'
                      name='backend'
                      value='supabase'
                      checked={backendType === 'supabase'}
                      onChange={() => {
                        setBackendTouched(true);
                        setBackendType('supabase');
                      }}
                      className='mt-1 shrink-0'
                      disabled={anyLoading}
                    />
                    <div className='flex-1 min-w-0'>
                      <div className='text-sm font-medium text-gray-200'>
                        {t('codable.backendCloud')}
                      </div>
                      <div className='text-xs text-gray-500 mt-0.5'>
                        {t('codable.backendCloudHint')}
                      </div>
                    </div>
                  </label>
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      backendType === 'none'
                        ? 'border-blue-500 bg-blue-500/5'
                        : 'border-gray-800 bg-gray-950 hover:border-gray-700'
                    }`}
                  >
                    <input
                      type='radio'
                      name='backend'
                      value='none'
                      checked={backendType === 'none'}
                      onChange={() => {
                        setBackendTouched(true);
                        setBackendType('none');
                      }}
                      className='mt-1 shrink-0'
                      disabled={anyLoading}
                    />
                    <div className='flex-1 min-w-0'>
                      <div className='text-sm font-medium text-gray-200'>
                        {t('codable.backendFeOnly')}
                      </div>
                      <div className='text-xs text-gray-500 mt-0.5'>
                        {t('codable.backendFeOnlyHint')}
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className='rounded-xl border border-gray-800 bg-gray-950/40 p-4'>
                <StackFlowGraphic
                  title={t('codable.stackPreview', 'Stack, který Orchestrátor založí')}
                  services={activeOperation ? operationStack : newTabStack}
                />
              </div>

              <div className='flex flex-wrap items-center gap-3 pt-2'>
                <Button
                  variant='primary'
                  icon={<CodableIcon size={16} />}
                  onClick={() => startMut.mutate('apply')}
                  loading={startMut.isPending || operationRunning}
                  // Blokátor prerekvizit (chybí identita, nesmíš zakládat) je
                  // důvod nezačínat — ne až hláška po selhání stavby.
                  disabled={anyLoading || createBlockers.length > 0}
                >
                  {startMut.isPending || operationRunning
                    ? t('codable.provisioning')
                    : t('codable.createProject')}
                </Button>
                <Button
                  variant='secondary'
                  onClick={() => startMut.mutate('draft')}
                  loading={startMut.isPending && startMut.variables === 'draft'}
                  disabled={anyLoading}
                >
                  {t('codable.saveDraft', 'Uložit zadání bez realizace')}
                </Button>
                {createBlockers.length > 0 && (
                  <span className='text-xs text-amber-300'>
                    {t('codable.prereqBlockedHint', 'Nejdřív vyřeš prerekvizity výše.')}
                  </span>
                )}
                <span className='text-xs text-gray-500'>
                  {backendType === 'supabase'
                    ? t('codable.startTakesLong')
                    : t('codable.startTakesShort')}
                </span>
              </div>

              {activeOperation && (
                <div
                  className={`rounded-lg border p-4 space-y-3 ${
                    activeOperation.status === 'failed'
                      ? 'border-red-500/40 bg-red-500/5'
                      : activeOperation.status === 'draft'
                      ? 'border-amber-500/40 bg-amber-500/5'
                      : activeOperation.status === 'completed'
                      ? 'border-emerald-500/40 bg-emerald-500/5'
                      : 'border-blue-500/30 bg-blue-500/5'
                  }`}
                >
                  <div className='flex items-center justify-between gap-3 text-sm'>
                    <span className='font-medium text-gray-200'>
                      {activeOperation.slug} · {activeOperation.phase}
                    </span>
                    <span className='font-mono text-gray-400'>
                      {activeOperation.progress}%
                    </span>
                  </div>
                  <div className='h-2 overflow-hidden rounded-full bg-gray-900'>
                    <div
                      className={`h-full transition-all ${
                        activeOperation.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                      }`}
                      style={{
                        width: `${Math.max(0, Math.min(100, activeOperation.progress))}%`,
                      }}
                    />
                  </div>
                  <StackFlowGraphic services={operationStack} />
                  {activeOperation.error && (
                    <div className='text-sm text-red-300 whitespace-pre-wrap'>
                      {activeOperation.error}
                    </div>
                  )}
                  {/* Pre-flight blokátory nesou konkrétní návod („spusť
                      install-supabase-template.sh") — vypsat je čitelně, ne jako
                      surový JSON, jinak z toho operátor přečte jen „selhalo". */}
                  {activeOperation.status === 'failed' &&
                    (activeOperation.result?.preflight?.blockers?.length ?? 0) > 0 && (
                    <ul className='space-y-1 text-sm text-amber-300'>
                      {activeOperation.result!.preflight!.blockers!.map((b) => (
                        <li key={`${b.code}-${b.service ?? 'node'}`}>
                          {b.service ? `${b.service}: ` : ''}
                          {b.message}
                          {b.node ? ` (${t('codable.nodeLabel', 'uzel')} ${b.node})` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                  {(activeOperation.result?.hint ?? activeOperation.result?.cleanup_hint) && (
                    <div className='text-sm text-gray-400'>
                      {activeOperation.result?.hint ?? activeOperation.result?.cleanup_hint}
                    </div>
                  )}
                  {activeOperation.status === 'failed' &&
                    activeOperation.result?.apply_results !== undefined && (
                    <pre className='max-h-48 overflow-auto rounded bg-gray-950 p-3 text-xs text-gray-400'>
                      {JSON.stringify(activeOperation.result.apply_results, null, 2)}
                    </pre>
                  )}
                  {activeOperation.status === 'failed' && (
                    <Button
                      variant='secondary'
                      size='sm'
                      onClick={() => retryOperationMut.mutate()}
                      loading={retryOperationMut.isPending}
                    >
                      {t('codable.retryDraft', 'Zkusit znovu nad uloženým zadáním')}
                    </Button>
                  )}
                  {activeOperation.status === 'draft' && (
                    <div className='space-y-2'>
                      <div className='text-sm text-amber-200'>
                        {t(
                          'codable.draftHint',
                          'Zadání je bezpečně uložené v Orchestrátoru. Zatím nevzniklo repo, backend, frontend ani publikace.',
                        )}
                      </div>
                      <Button
                        variant='primary'
                        size='sm'
                        onClick={() => startDraftMut.mutate()}
                        loading={startDraftMut.isPending}
                      >
                        {t('codable.realizeDraft', 'Realizovat uložené zadání')}
                      </Button>
                    </div>
                  )}
                  <div className='text-[11px] font-mono text-gray-600'>
                    operation {activeOperation.id}
                  </div>
                </div>
              )}
              {activeOperationId && operationLoadError && (
                <div className='rounded-lg border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-300'>
                  {t('codable.operationLoadError', 'Nelze načíst průběh operace: {{msg}}', { msg: operationLoadError })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        <GitlabProjectsPanel
          search={search}
          onSearch={setSearch}
          data={userProjects.data}
          isLoading={userProjects.isLoading}
          selectedKey={inspectProject ? projectKey(inspectProject) : null}
          onInspect={(p) => {
            // Analýza až po prerekvizitách: číst cizí repozitář nemá smysl
            // dřív, než je jisté, že na něj volající vůbec má právo.
            setInspectProject(p);
            analyzeMut.reset();
          }}
          inspectingKey={
            analyzeMut.isPending && analyzeMut.variables?.project
              ? projectKey(analyzeMut.variables.project)
              : null
          }
          anyLoading={anyLoading}
          onSetupGitlab={() => {
            setOnboardingIntent('list');
            setShowOnboarding(true);
          }}
        />
        </>
      )}

      {tab === 'import' && (
        <div className='space-y-4'>
          {importSource !== 'choose' && (
            <Button
              variant='ghost'
              size='sm'
              icon={<ArrowLeft size={14} />}
              onClick={() => setImportSource('choose')}
              disabled={anyLoading}
            >
              {t('codable.importBackSources')}
            </Button>
          )}

          {importSource === 'choose' && (
            <Card>
              <CardContent>
                <div className='space-y-3'>
                  <div>
                    <h3 className='text-sm font-semibold text-gray-200'>
                      {t('codable.importWizardTitle')}
                    </h3>
                    <p className='text-xs text-gray-500 mt-1 leading-relaxed'>
                      {t('codable.importWizardIntro')}
                    </p>
                  </div>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                    <ImportSourceCard
                      icon={<GitBranch size={18} />}
                      title={t('codable.importSrcGitlab')}
                      desc={t('codable.importSrcGitlabDesc')}
                      onClick={() => setImportSource('gitlab')}
                    />
                    <ImportSourceCard
                      icon={<Wand2 size={18} />}
                      title={t('codable.importSrcLovable')}
                      desc={t('codable.importSrcLovableDesc')}
                      accent='purple'
                      onClick={() => setImportSource('lovable')}
                    />
                    <ImportSourceCard
                      icon={<HardDrive size={18} />}
                      title={t('codable.importSrcAcm')}
                      desc={t('codable.importSrcAcmDesc')}
                      onClick={() => {
                        setImportSource('acm');
                        setAcmModalOpen(true);
                      }}
                    />
                    <ImportSourceCard
                      icon={<Link2 size={18} />}
                      title={t('codable.importSrcGitUrl')}
                      desc={t('codable.importSrcGitUrlDesc')}
                      onClick={() => setImportSource('giturl')}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {importSource === 'gitlab' && (
            <GitlabProjectsPanel
              search={search}
              onSearch={setSearch}
              data={userProjects.data}
              isLoading={userProjects.isLoading}
              selectedKey={inspectProject ? projectKey(inspectProject) : null}
              onInspect={(p) => {
                // Stejně jako na záložce Nový: nejdřív prerekvizity, pak stack.
                setInspectProject(p);
                analyzeMut.reset();
              }}
              inspectingKey={
                analyzeMut.isPending && analyzeMut.variables?.project
                  ? projectKey(analyzeMut.variables.project)
                  : null
              }
              anyLoading={anyLoading}
              onSetupGitlab={() => {
                setOnboardingIntent('list');
                setShowOnboarding(true);
              }}
            />
          )}

          {importSource === 'lovable' && (
            <Card>
              <CardContent>
                <div className='space-y-4'>
                  <div>
                    <h3 className='text-sm font-semibold text-gray-200 flex items-center gap-2'>
                      <Wand2 size={16} className='text-purple-400' />
                      {t('codable.importSrcLovable')}
                    </h3>
                    <p className='text-xs text-gray-500 mt-1 leading-relaxed'>
                      {t('codable.lovableFormHint')}
                    </p>
                  </div>

                  {/* Import z cizí infrastruktury — řekni to rovnou, ne až
                      když něco nesedí. */}
                  <div className='rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 space-y-1'>
                    <div className='flex items-center gap-2 text-xs font-medium text-amber-200'>
                      <AlertTriangle size={13} className='flex-shrink-0' />
                      {t('codable.lovableDisclaimerTitle')}
                    </div>
                    <p className='text-[11px] leading-relaxed text-amber-100/80'>
                      {t('codable.lovableDisclaimerBody')}
                    </p>
                    <p className='text-[11px] leading-relaxed text-amber-100/80'>
                      {t('codable.lovableDisclaimerAgent')}
                    </p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-300 mb-1.5'>
                      {t('codable.lovableGitUrl')}
                    </label>
                    <input
                      className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm font-mono text-gray-200 placeholder-gray-600 focus:border-purple-500 focus:outline-none'
                      placeholder='https://github.com/…/…  nebo GitLab export z Lovable'
                      value={lovableUrl}
                      onChange={(e) => setLovableUrl(e.target.value)}
                      disabled={anyLoading}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-300 mb-1.5'>
                      {t('codable.optionalSlug')}
                    </label>
                    <input
                      className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm font-mono text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none'
                      placeholder='my-app'
                      value={lovableSlug}
                      onChange={(e) => setLovableSlug(e.target.value)}
                      disabled={anyLoading}
                    />
                  </div>
                  <div className='flex flex-col sm:flex-row gap-2'>
                    <Button
                      variant='secondary'
                      className='w-full sm:w-auto justify-center'
                      disabled={!lovableUrl.trim() || previewMut.isPending}
                      loading={previewMut.isPending}
                      onClick={() => previewMut.mutate()}
                    >
                      {t('codable.previewStack', 'Náhled stacku')}
                    </Button>
                    <Button
                      variant='primary'
                      className='w-full sm:w-auto justify-center'
                      disabled={!lovableUrl.trim() || anyLoading}
                      loading={urlImportMut.isPending}
                      onClick={() =>
                        urlImportMut.mutate({
                          gitUrl: lovableUrl,
                          slug: lovableSlug || undefined,
                          source: 'lovable',
                          channel: 'ui-lovable-import',
                        })}
                    >
                      {t('codable.lovableStartImport')}
                    </Button>
                    <Button
                      variant='secondary'
                      className='w-full sm:w-auto justify-center'
                      icon={<Wand2 size={14} />}
                      onClick={() => {
                        setWizardInitial(undefined);
                        setAdvancedOpen(true);
                      }}
                    >
                      {t('codable.lovableAdvancedWizard')}
                    </Button>
                  </div>
                  {/* Který import kdy — jinak je „pokročilý" jen tlačítko bez
                      významu a člověk hádá, o co přichází. */}
                  <p className='text-[11px] text-gray-500 leading-relaxed'>
                    {t('codable.lovableWhichImport')}
                  </p>
                  {lovableSpecError && (
                    <div className='text-sm text-red-300'>{lovableSpecError}</div>
                  )}
                  {(lovableSpec || activeOperation) && (
                    <div className='rounded-xl border border-purple-500/20 bg-purple-950/10 p-4'>
                      <StackFlowGraphic
                        title={t('codable.stackPreview', 'Stack, který Orchestrátor založí')}
                        services={activeOperation ? operationStack : stackFromWizardSpec(lovableSpec)}
                      />
                    </div>
                  )}
                  <p className='text-[11px] text-gray-600 leading-relaxed'>
                    {t('codable.lovableOrchNote')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {importSource === 'acm' && (
            <Card>
              <CardContent>
                <div className='space-y-3'>
                  <h3 className='text-sm font-semibold text-gray-200 flex items-center gap-2'>
                    <HardDrive size={16} className='text-blue-400' />
                    {t('codable.importSrcAcm')}
                  </h3>
                  <p className='text-xs text-gray-500 leading-relaxed'>
                    {t('codable.importSrcAcmLong')}
                  </p>
                  <Button
                    variant='primary'
                    icon={<HardDrive size={14} />}
                    onClick={() => setAcmModalOpen(true)}
                  >
                    {t('codable.openAcmImport')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {importSource === 'giturl' && (
            <Card>
              <CardContent>
                <div className='space-y-4'>
                  <div>
                    <h3 className='text-sm font-semibold text-gray-200 flex items-center gap-2'>
                      <Link2 size={16} className='text-emerald-400' />
                      {t('codable.importSrcGitUrl')}
                    </h3>
                    <p className='text-xs text-gray-500 mt-1 leading-relaxed'>
                      {t('codable.gitUrlFormHint')}
                    </p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-300 mb-1.5'>
                      {t('codable.gitRepoUrl')}
                    </label>
                    <input
                      className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm font-mono text-gray-200 placeholder-gray-600 focus:border-emerald-500 focus:outline-none'
                      placeholder='https://gitlab.example.com/group/repo.git'
                      value={gitUrl}
                      onChange={(e) => setGitUrl(e.target.value)}
                      disabled={anyLoading}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-300 mb-1.5'>
                      {t('codable.optionalSlug')}
                    </label>
                    <input
                      className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm font-mono text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none'
                      placeholder='my-app'
                      value={gitUrlSlug}
                      onChange={(e) => setGitUrlSlug(e.target.value)}
                      disabled={anyLoading}
                    />
                  </div>
                  <Button
                    variant='primary'
                    className='w-full sm:w-auto justify-center'
                    disabled={!gitUrl.trim() || anyLoading}
                    loading={urlImportMut.isPending}
                    onClick={() =>
                      urlImportMut.mutate({
                        gitUrl,
                        slug: gitUrlSlug || undefined,
                        source: 'native',
                        channel: 'ui-giturl-import',
                      })}
                  >
                    {t('codable.importActionBtn')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Progress (shared across import paths) */}
          {activeOperation && tab === 'import' && (
            <Card>
              <CardContent>
                <div
                  className={`rounded-lg border p-4 space-y-3 ${
                    activeOperation.status === 'failed'
                      ? 'border-red-500/40 bg-red-500/5'
                      : activeOperation.status === 'completed'
                      ? 'border-emerald-500/40 bg-emerald-500/5'
                      : 'border-blue-500/30 bg-blue-500/5'
                  }`}
                >
                  <div className='flex items-center justify-between gap-3 text-sm'>
                    <span className='font-medium text-gray-200'>
                      {activeOperation.slug} · {activeOperation.phase}
                    </span>
                    <span className='font-mono text-gray-400'>
                      {activeOperation.progress}%
                    </span>
                  </div>
                  <div className='h-2 overflow-hidden rounded-full bg-gray-900'>
                    <div
                      className={`h-full transition-all ${
                        activeOperation.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                      }`}
                      style={{
                        width: `${Math.max(0, Math.min(100, activeOperation.progress))}%`,
                      }}
                    />
                  </div>
                  <StackFlowGraphic services={operationStack} />
                  {activeOperation.error && (
                    <div className='text-sm text-red-300 whitespace-pre-wrap'>
                      {activeOperation.error}
                    </div>
                  )}
                  {activeOperation.status === 'failed' && (
                    <Button
                      variant='secondary'
                      size='sm'
                      onClick={() => retryOperationMut.mutate()}
                      loading={retryOperationMut.isPending}
                    >
                      {t('codable.retrySavedOp')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          {activeOperationId && operationLoadError && tab === 'import' && (
            <div className='rounded-lg border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-300'>
              {t('codable.opLoadError', { msg: operationLoadError })}
            </div>
          )}

          {acmModalOpen && (
            <AcmImportModal
              onClose={() => setAcmModalOpen(false)}
              onImported={() => {
                setAcmModalOpen(false);
                toast.success(t('codable.acmImportDone'));
                nav('/modules/codable');
              }}
            />
          )}
        </div>
      )}

      {showOnboarding && (
        <GitlabOnboardingModal
          onReady={() => {
            setShowOnboarding(false);
            void userProjects.refetch();
            if (onboardingIntent === 'create' && tab === 'new') {
              startMut.mutate('apply');
            }
          }}
          onCancel={() => setShowOnboarding(false)}
        />
      )}
      {advancedOpen && (
        <LovableImportWizard
          enableCodable
          initial={wizardInitial}
          onClose={() => setAdvancedOpen(false)}
          onDone={() => {
            setAdvancedOpen(false);
            nav('/modules/codable');
          }}
        />
      )}
    </div>
  );
}

function RepoAnalysisCard({
  project,
  preflight,
  analysis,
  loading,
  error,
  frontend,
  backend,
  onFrontend,
  onBackend,
  importAsLovable,
  onImportAsLovable,
  aiProfileId,
  onAiProfile,
  onAskAi,
  askingAi,
  onCreate,
  creating,
  onCloudWizard,
  onClose,
}: {
  project: UserProject;
  /** Sdílený pre-flight z kroku PREREKVIZITY — stejná odpověď, ne druhý dotaz. */
  preflight: UseQueryResult<PreflightResp>;
  analysis?: RepoAnalysis;
  loading: boolean;
  error: string | null;
  frontend: 'react' | 'node' | 'deno' | 'laravel' | 'none';
  backend: 'supabase' | 'postgres' | 'mysql' | 'mariadb' | 'none';
  onFrontend: (v: 'react' | 'node' | 'deno' | 'laravel' | 'none') => void;
  onBackend: (v: 'supabase' | 'postgres' | 'mysql' | 'mariadb' | 'none') => void;
  importAsLovable: boolean;
  onImportAsLovable: (v: boolean) => void;
  aiProfileId: string;
  onAiProfile: (v: string) => void;
  onAskAi: () => void;
  askingAi: boolean;
  onCreate: () => void;
  creating: boolean;
  onCloudWizard: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const profiles = analysis?.ai_help?.profiles ?? [];
  // Vejde se to sem? Odpověď patří NA tuhle obrazovku, ne až do odmítnutí
  // založení — a když se vejde, je to dobrá zpráva, kterou má člověk vidět
  // dřív než seznam požadavků. Ptá se na to krok PREREKVIZITY, tahle karta
  // stejnou odpověď jen dopovídá o rozpočet a kvótu.
  const preflightQ = preflight;
  const pre = preflightQ.data;
  return (
    <Card>
      <CardContent>
        <div className='space-y-4'>
          <div className='flex items-start justify-between gap-3'>
            <div>
              <h3 className='text-sm font-semibold text-gray-100'>
                {t('codable.stackFromRepo', 'Stack z repozitáře')}
              </h3>
              <p className='text-xs text-gray-500 mt-1 font-mono truncate'>
                {project.path}
              </p>
            </div>
            <Button variant='ghost' size='sm' onClick={onClose}>
              {t('common.close', 'Zavřít')}
            </Button>
          </div>

          {loading && !askingAi && (
            <div className='flex items-center gap-2 text-sm text-gray-400'>
              <Loader2 size={16} className='animate-spin' />
              {t('codable.inspectingRepo', 'Načítám soubory a zjišťuju stack…')}
            </div>
          )}
          {error && <div className='text-sm text-red-300'>{error}</div>}

          {analysis?.ok && (
            <>
              <div className='flex flex-wrap items-center gap-2 text-xs'>
                <span className={`px-2 py-0.5 rounded border ${
                  analysis.confidence === 'high'
                    ? 'border-emerald-500/40 text-emerald-300'
                    : analysis.confidence === 'medium'
                    ? 'border-amber-500/40 text-amber-300'
                    : 'border-gray-700 text-gray-400'
                }`}>
                  {t('codable.confidence', 'jistota')}: {analysis.confidence}
                </span>
                {analysis.frontend?.framework && (
                  <span className='text-gray-400'>{analysis.frontend.framework}</span>
                )}
                {analysis.lovable?.detected && (
                  <span className='text-purple-300'>Lovable</span>
                )}
              </div>

              {analysis.acm?.managed && analysis.acm.marker && (
                <div className='rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 space-y-1'>
                  <div className='font-medium'>
                    {t('codable.repoAlreadyAcm', 'Tenhle repozitář už spravuje ACM')}
                  </div>
                  <div className='text-amber-200/80'>
                    {t('codable.repoAlreadyAcmDetail', {
                      slug: analysis.acm.marker.slug,
                      version: analysis.acm.marker.acm_version,
                      defaultValue:
                        'Vznikl jako projekt „{{slug}}" v ACM {{version}}. Importem by vznikl podruhé — ' +
                        'nejdřív se podívej, jestli ho nemáš v Codable.',
                    })}
                  </div>
                </div>
              )}
              {analysis.lovable?.detected && (
                <div className='rounded-lg border border-purple-500/30 bg-purple-500/10 px-3 py-2 text-xs text-purple-200 space-y-2'>
                  <div className='font-medium'>
                    {t('codable.repoIsLovable', 'Tohle je projekt z Lovable')}
                  </div>
                  <div className='text-purple-200/80'>
                    {t(
                      'codable.repoIsLovableDetail',
                      'Import z Gitu vezme jen kód. Schéma, data a napojení na cloudový backend ' +
                        'umí jen import z Lovable — tenhle projekt jím pusť.',
                    )}
                  </div>
                  <Button variant='secondary' size='sm' onClick={onCloudWizard}>
                    {t('codable.repoIsLovableAction', 'Přepnout na import z Lovable')}
                  </Button>
                </div>
              )}

              {(preflightQ.isLoading || pre) && (
                <div
                  className={`rounded-lg border px-3 py-2 text-xs ${
                    preflightQ.isLoading
                      ? 'border-gray-800 bg-gray-950/60 text-gray-400'
                      : pre?.fits
                      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                      : 'border-red-500/30 bg-red-500/10 text-red-200'
                  }`}
                >
                  {preflightQ.isLoading
                    ? (
                      <span className='flex items-center gap-2'>
                        <Loader2 size={12} className='animate-spin' />
                        {t('codable.preflightChecking', 'Počítám, jestli se to sem vejde…')}
                      </span>
                    )
                    : (
                      <>
                        <div className='flex items-center gap-2 font-medium'>
                          {pre?.fits
                            ? <CheckCircle2 size={13} className='flex-shrink-0' />
                            : <AlertTriangle size={13} className='flex-shrink-0' />}
                          <span>{pre?.verdict}</span>
                        </div>
                        {!!pre?.quota?.remaining_human && (
                          <div className='mt-1 text-[11px] opacity-80'>
                            {t('codable.preflightQuota', {
                              remaining: pre.quota.remaining_human,
                              limit: pre.quota.limit_human ?? '',
                              defaultValue: 'Tenantovi zbývá {{remaining}} z {{limit}}.',
                            })}
                          </div>
                        )}
                        {!!pre?.budget?.unpriced?.length && (
                          <div className='mt-1 text-[11px] opacity-80'>
                            {t('codable.preflightUnpriced', {
                              services: pre.budget.unpriced.join(', '),
                              defaultValue:
                                'Odhad je nižší než skutečnost — bez profilu: {{services}}.',
                            })}
                          </div>
                        )}
                        {(pre?.blockers ?? []).map((b) => (
                          <div key={b.code} className='mt-1 text-[11px]'>{b.message}</div>
                        ))}
                        {(pre?.warnings ?? []).map((w) => (
                          <div key={w.code} className='mt-1 text-[11px] opacity-80'>{w.message}</div>
                        ))}
                      </>
                    )}
                </div>
              )}

              {!!analysis.access?.length && (
                <details className='rounded-lg border border-gray-800 bg-gray-950/60 px-3 py-2'>
                  <summary className='cursor-pointer text-xs text-gray-300'>
                    {t('codable.accessSurvey', 'Co bude potřeba sehnat')}{' '}
                    <span className='text-gray-500'>
                      ({analysis.access.filter((x) => x.from === 'user').length})
                    </span>
                  </summary>
                  <p className='mt-2 text-[11px] text-gray-500'>
                    {t(
                      'codable.accessSurveyHint',
                      'Průzkum zdrojáků — projít to teď nemusíš, import poběží i tak. ' +
                        'Jde o to, abys dopředu věděl, co tě čeká.',
                    )}
                  </p>
                  <ul className='mt-2 space-y-1.5'>
                    {analysis.access.filter((x) => x.from === 'user').map((need) => (
                      <li key={need.code} className='text-[11px] text-gray-300'>
                        <span className='font-medium'>{need.label}</span>
                        {need.optional && (
                          <span className='ml-1 text-gray-500'>
                            {t('codable.accessOptional', '(nepovinné)')}
                          </span>
                        )}
                        {!need.transferable && (
                          <span className='ml-1 text-amber-300'>
                            {t('codable.accessNotTransferable', '· nepřenese se')}
                          </span>
                        )}
                        {need.note && (
                          <span className='block text-gray-500'>{need.note}</span>
                        )}
                        {need.evidence && (
                          <span className='block font-mono text-[10px] text-gray-600'>
                            {need.evidence}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <StackFlowGraphic
                title={t('codable.detectedStack', 'Zjištěný stack')}
                services={plannedStack({
                  frontend: frontend === 'none' ? undefined : frontend,
                  backend: backend === 'none' ? undefined : backend,
                  publish: true,
                  backup: backend !== 'none',
                  gitHint: project.path.split('/').pop(),
                })}
              />

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <label className='block text-xs text-gray-400'>
                  Frontend
                  <select
                    className='mt-1 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-200'
                    value={frontend}
                    onChange={(e) => onFrontend(e.target.value as typeof frontend)}
                  >
                    <option value='react'>React / Vite</option>
                    <option value='laravel'>Laravel</option>
                    <option value='node'>Node</option>
                    <option value='deno'>Deno</option>
                    <option value='none'>{t('codable.zadny', 'žádný')}</option>
                  </select>
                </label>
                <label className='block text-xs text-gray-400'>
                  Backend
                  <select
                    className='mt-1 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-200'
                    value={backend}
                    onChange={(e) => onBackend(e.target.value as typeof backend)}
                  >
                    <option value='supabase'>Supabase</option>
                    <option value='mysql'>MySQL</option>
                    <option value='mariadb'>MariaDB</option>
                    <option value='postgres'>Postgres</option>
                    <option value='none'>{t('codable.zadny', 'žádný')}</option>
                  </select>
                </label>
              </div>

              {(analysis.evidence?.length ?? 0) > 0 && (
                <ul className='text-xs text-gray-500 space-y-0.5'>
                  {analysis.evidence!.slice(0, 8).map((e) => <li key={e}>· {e}</li>)}
                </ul>
              )}
              {(analysis.questions?.length ?? 0) > 0 && (
                <ul className='text-xs text-amber-300 space-y-0.5'>
                  {analysis.questions!.map((q) => <li key={q}>{q}</li>)}
                </ul>
              )}

              <label className='flex items-start gap-2 text-xs text-gray-400 cursor-pointer select-none'>
                <input
                  type='checkbox'
                  checked={importAsLovable}
                  onChange={(e) => onImportAsLovable(e.target.checked)}
                  className='accent-purple-500 mt-0.5'
                />
                <span>{t('codable.lovableToggle')}</span>
              </label>

              <div className='rounded-xl border border-gray-800 bg-gray-950/40 p-3 space-y-2'>
                <div className='text-xs text-gray-400'>
                  {t(
                    'codable.aiHelpHint',
                    'Stack se bere ze souborů, bez AI. Model můžeš zavolat jen když si nejsi jistý.',
                  )}
                </div>
                <div className='flex flex-col sm:flex-row gap-2'>
                  <select
                    className='flex-1 bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-200'
                    value={aiProfileId}
                    onChange={(e) => onAiProfile(e.target.value)}
                  >
                    {profiles.length === 0 && (
                      <option value=''>
                        {t('codable.aiNoModel', 'Žádný povolený Codable model')}
                      </option>
                    )}
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.id === analysis.ai_help?.default_profile_id
                          ? `${p.label} (${t('codable.aiDefault', 'výchozí')})`
                          : p.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant='secondary'
                    size='sm'
                    onClick={onAskAi}
                    loading={askingAi}
                    disabled={!aiProfileId && profiles.length === 0}
                  >
                    {t('codable.askCodableModel', 'Zavolat Codable model')}
                  </Button>
                </div>
                {analysis.ai?.error && (
                  <div className='text-xs text-red-300'>{analysis.ai.error}</div>
                )}
                {analysis.ai?.suggestion?.notes && (
                  <div className='text-xs text-gray-300'>{analysis.ai.suggestion.notes}</div>
                )}
              </div>

              <div className='flex flex-col sm:flex-row flex-wrap gap-2'>
                <Button
                  variant='primary'
                  onClick={onCreate}
                  loading={creating}
                  disabled={creating || preflightQ.isFetching || pre?.fits === false}
                >
                  {t('codable.createFromStack', 'Založit podle stacku')}
                </Button>
                {analysis.lovable?.detected && (
                  <Button variant='secondary' onClick={onCloudWizard}>
                    {t('codable.continueCloudData', 'Pokračovat: schema + data z cloudu')}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function hostLabel(host: string): string {
  return host.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}

function projectKey(p: UserProject): string {
  return `${p.connection_id ?? 'gl'}:${p.id}`;
}

function GitlabProjectsPanel({
  search,
  onSearch,
  data,
  isLoading,
  selectedKey,
  onInspect,
  inspectingKey,
  anyLoading,
  onSetupGitlab,
}: {
  search: string;
  onSearch: (value: string) => void;
  data?: ProjectsListResp;
  isLoading: boolean;
  selectedKey: string | null;
  onInspect: (project: UserProject) => void;
  inspectingKey: string | null;
  anyLoading: boolean;
  onSetupGitlab: () => void;
}) {
  const { t } = useTranslation();
  const needsAuth = data?.setup_required === 'gitlab_credentials';
  const projects = data?.projects ?? [];
  const connections = data?.connections ?? [];
  const grouped = (() => {
    const byHost = new Map<string, UserProject[]>();
    for (const p of projects) {
      const host = p.host || 'GitLab';
      const arr = byHost.get(host) ?? [];
      arr.push(p);
      byHost.set(host, arr);
    }
    if (connections.length > 0) {
      return connections.map((c) => ({
        host: c.host,
        error: c.ok ? undefined : c.error,
        items: byHost.get(c.host) ?? [],
      }));
    }
    return [...byHost.entries()].map(([host, items]) => ({ host, items, error: undefined as string | undefined }));
  })();

  return (
    <Card>
      <CardContent>
        <div className='space-y-4'>
          <div>
            <label className='block text-sm font-medium text-gray-300 mb-1.5'>
              {t('codable.gitlabFromConnected', 'Projekty z propojených GitLabů')}
            </label>
            <div className='relative'>
              <Search
                size={14}
                className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-600'
              />
              <input
                className='w-full bg-gray-950 border border-gray-800 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none'
                placeholder={t('codable.searchYourProjects')}
                value={search}
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
            <div className='text-xs text-gray-500 mt-1'>
              {t(
                'codable.gitlabFromConnectedHint',
                'Repa, ke kterým máš oprávnění. Nejdřív načteme soubory a určíme stack, pak tě provede průvodce.',
              )}
            </div>
          </div>

          {needsAuth && (
            <div className='bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm space-y-2'>
              <div className='text-blue-300 font-medium'>
                {t('codable.missingGitlabToken')}
              </div>
              <p className='text-gray-400 text-xs'>
                {t(
                  'codable.gitlabConnectToList',
                  'Správce propojí tvoji SSO identitu s GitLabem. Osobní PAT není potřeba.',
                )}
              </p>
              <Button
                variant='primary'
                size='sm'
                onClick={onSetupGitlab}
                icon={<ExternalLink size={14} />}
              >
                {t('codable.setupGitlabAccess')}
              </Button>
            </div>
          )}

          {isLoading && (
            <div className='flex items-center gap-2 text-gray-500 text-sm py-6'>
              <Loader2 size={16} className='animate-spin' /> {t('codable.loadingYourProjects')}
            </div>
          )}

          {!needsAuth && !isLoading && grouped.length === 0 && (
            <div className='text-sm text-gray-500 py-6 text-center'>
              {t('codable.noGitlabProjects')}
            </div>
          )}

          {!needsAuth && grouped.length > 0 && (
            <>
              <div className='space-y-4 max-h-[min(32rem,60vh)] overflow-y-auto'>
                {grouped.map((group) => (
                  <div key={group.host} className='space-y-2'>
                    <div className='flex items-center justify-between gap-2'>
                      <div className='text-[11px] uppercase tracking-wide text-gray-500 font-medium'>
                        {hostLabel(group.host)}
                      </div>
                      {group.error && (
                        <div className='text-[11px] text-amber-400'>
                          {group.error}
                        </div>
                      )}
                    </div>
                    {group.items.length === 0 && !group.error && (
                      <div className='text-xs text-gray-600 px-1'>
                        {t('codable.gitlabNoProjectsOnHost', 'Na {{host}} teď nic neodpovídá.', {
                          host: hostLabel(group.host),
                        })}
                      </div>
                    )}
                    {group.items.map((p) => (
                      <div
                        key={projectKey(p)}
                        className={`border rounded-lg p-3 flex flex-col sm:flex-row sm:items-start justify-between gap-3 ${
                          p.already_imported
                            ? 'border-emerald-500/20 bg-emerald-500/5'
                            : selectedKey === projectKey(p)
                            ? 'border-blue-500 bg-blue-500/5'
                            : 'border-gray-800 bg-gray-950 hover:border-blue-500/40'
                        }`}
                      >
                        <div className='min-w-0 flex-1'>
                          <div className='flex items-center gap-2 flex-wrap'>
                            <GitBranch size={14} className='text-gray-500 shrink-0' />
                            <span className='text-sm font-medium text-gray-200'>
                              {p.name}
                            </span>
                            {p.already_imported && (
                              <span className='text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300'>
                                {t('codable.importedBadge')}
                              </span>
                            )}
                          </div>
                          <div className='text-xs font-mono text-gray-500 mt-0.5 truncate'>
                            {p.path}
                          </div>
                          {p.description && (
                            <div className='text-xs text-gray-400 mt-1 line-clamp-2'>
                              {p.description}
                            </div>
                          )}
                        </div>
                        <Button
                          variant='primary'
                          size='sm'
                          className='w-full sm:w-auto shrink-0 justify-center'
                          onClick={() => onInspect(p)}
                          disabled={p.already_imported || anyLoading}
                          loading={inspectingKey === projectKey(p)}
                        >
                          {p.already_imported
                            ? t('codable.done')
                            : t('codable.inspectRepo', 'Načíst a zjistit stack')}
                        </Button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center gap-2 -mb-px ${
        active
          ? 'border-blue-500 text-blue-400'
          : 'border-transparent text-gray-500 hover:text-gray-300'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function ImportSourceCard({
  icon,
  title,
  desc,
  onClick,
  accent = 'blue',
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  onClick: () => void;
  accent?: 'blue' | 'purple';
}) {
  const iconCls = accent === 'purple'
    ? 'bg-purple-500/15 text-purple-300'
    : 'bg-blue-500/15 text-blue-300';
  return (
    <button
      type='button'
      onClick={onClick}
      className='text-left rounded-2xl border border-gray-800 bg-gray-950/60 p-4 hover:border-gray-600 hover:bg-gray-900/50 transition-colors group'
    >
      <div className={`inline-flex rounded-xl p-2.5 mb-3 ${iconCls}`}>
        {icon}
      </div>
      <div className='text-sm font-semibold text-gray-100 group-hover:text-white'>
        {title}
      </div>
      <p className='text-xs text-gray-500 mt-1 leading-relaxed'>{desc}</p>
    </button>
  );
}
